import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import {
  AlertTriangle, CalendarDays, CheckCircle2, Clock3, FileQuestion,
  FileVideo2, FolderOpen, History, Pencil, RefreshCw, Search, Trash2,
} from 'lucide-react'
import type { Program, ScheduleItem } from '../../types/index.js'

interface SelectedMedia {
  filePath: string
  fileName: string
  codec: string | null
  container: string | null
  durationSec: number | null
  resolution: string | null
  fileSizeBytes: string | null
}
import { useScheduleActions } from '../../hooks/useSchedule.js'
import { itemsApi, programsApi, recurrenceApi, type MediaCheckResult } from '../../lib/api.js'
import { adBlocksApi } from '../../lib/adsApi.js'
import { stripProgramCodePrefix } from '../../lib/program-code.js'
import { liveItemPartTimeline } from '../../lib/livePartPlan.js'
import {
  formatTime, formatDuration, formatDurationShort,
  ITEM_STATUS_LABELS, ITEM_TYPE_LABELS,
} from '../../lib/utils.js'
import { useActiveChannelId, useStore } from '../../store/index.js'
import { AdBlockEditModal } from './AdBlockEditModal.js'
import { MediaBrowserModal } from '../programs/MediaBrowserModal.js'
import { DrawerShell } from '../common/DrawerShell.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ItemDetailPanelProps {
  item:          ScheduleItem
  onClose:       () => void
  onEdit:        (item: ScheduleItem) => void
  onItemUpdated: (item: ScheduleItem) => void
  canEdit?:       boolean
  canDelete?:     boolean
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const ItemDetailPanel = memo(function ItemDetailPanel({
  item, onClose, onEdit, onItemUpdated, canEdit: canEditPermission = true, canDelete = true,
}: ItemDetailPanelProps) {
  const { deleteItem } = useScheduleActions()
  const pushToast = useStore(s => s.pushToast)
  const incrementGridUpdateSeq = useStore(s => s.incrementGridUpdateSeq)
  const channelId = useActiveChannelId()
  const [deleting, setDeleting]         = useState(false)
  const [visible, setVisible]           = useState(false)
  const [showRecDialog, setShowRecDialog] = useState(false)
  const [showBlockEditor, setShowBlockEditor] = useState(false)
  const [adOrder, setAdOrder] = useState(() => item.adPlacements ?? [])
  const [savedAdOrderIds, setSavedAdOrderIds] = useState(() => (item.adPlacements ?? []).map(p => p.id))
  const [draggedAdId, setDraggedAdId] = useState<string | null>(null)
  const [savingAdOrder, setSavingAdOrder] = useState(false)
  const [adOrderError, setAdOrderError] = useState<string | null>(null)
  const [programDetail, setProgramDetail] = useState<Program | null>(null)
  const [programLoading, setProgramLoading] = useState(false)
  const [programError, setProgramError] = useState<string | null>(null)
  const [mediaBusy, setMediaBusy] = useState<'auto' | 'browse' | 'check' | null>(null)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [mediaCheck, setMediaCheck] = useState<MediaCheckResult | null>(null)
  const [mediaBrowserOpen, setMediaBrowserOpen] = useState(false)

  // Animation d'entrée
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    setAdOrder(item.adPlacements ?? [])
    setSavedAdOrderIds((item.adPlacements ?? []).map(p => p.id))
    setAdOrderError(null)
  }, [item.id, item.adPlacements])

  const loadProgramDetail = useCallback(async () => {
    if (!item.programId) {
      setProgramDetail(null)
      setProgramError(null)
      return
    }
    setProgramLoading(true)
    setProgramError(null)
    try {
      setProgramDetail(await programsApi.getById(item.programId))
    } catch (err: any) {
      setProgramError(err?.message ?? 'Impossible de charger les diffusions précédentes.')
    } finally {
      setProgramLoading(false)
    }
  }, [item.programId])

  useEffect(() => {
    void loadProgramDetail()
  }, [loadProgramDetail])

  const handleDeleteClick = () => {
    if (item.recurrenceId) {
      setShowRecDialog(true)
    } else {
      handleDeleteSingle()
    }
  }

  const handleDeleteSingle = async () => {
    setShowRecDialog(false)
    const target = item.bundleId
      ? `la partie ${item.program?.partNumber ?? (item.bundleOrder ?? 0) + 1} de « ${item.program?.title ?? 'ce programme'} »`
      : `« ${item.program?.title ?? 'cet item'} »`
    if (!confirm(`Supprimer ${target} de la grille ?`)) return
    setDeleting(true)
    try {
      if (item.recurrenceId) {
        await recurrenceApi.deleteOne(item.id)
      } else {
        await deleteItem(item.id)
      }
      onClose()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteFromHere = async () => {
    setShowRecDialog(false)
    if (!confirm(`Supprimer cet événement et tous les suivants de la série ?`)) return
    setDeleting(true)
    try {
      await recurrenceApi.deleteFromHere(item.recurrenceId!, item.id)
      onClose()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteAll = async () => {
    setShowRecDialog(false)
    if (!confirm(`Supprimer toute la série récurrente ?`)) return
    setDeleting(true)
    try {
      await recurrenceApi.deleteAll(item.recurrenceId!)
      onClose()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleClose = useCallback(() => {
    setVisible(false)
    setTimeout(onClose, 200)  // Laisse l'animation se terminer
  }, [onClose])

  const isAdBlock = item.itemType === 'AD_BREAK'
  const adBlockTitle = isAdBlock ? 'Bloc Pubs' : null
  const overrideTitle = typeof item.overrides?.title === 'string' ? item.overrides.title : null
  const title     = adBlockTitle ?? item.program?.title ?? overrideTitle ?? item.liveSource?.name ?? item.interludeClip?.name ?? 'Item'
  const isLocked  = ['ON_AIR', 'DONE', 'LOADING'].includes(item.status)
  const canEdit   = item.status === 'SCHEDULED'
  const canUseScheduleEdit = canEdit && item.itemType !== 'AD_BREAK'
  const canEditBlock = isAdBlock && !isLocked
  const canManageMedia = canEdit && item.itemType !== 'LIVE' && Boolean(item.programId)
  // Déroulé estimé du bloc, calé sur l'antenne (`lib/livePartPlan.ts`).
  const livePartTimeline = useMemo(() => liveItemPartTimeline(item), [item])
  const scheduledDate = useMemo(() => new Date(item.startTime), [item.startTime])
  const scheduledDateLabel = useMemo(() => scheduledDate.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }), [scheduledDate])
  const programMedia = useMemo(
    () => (programDetail?.mediaAssets ?? []).filter(asset => !asset.isProxy),
    [programDetail?.mediaAssets],
  )
  const expectedMediaNames = useMemo(
    () => item.program?.programCode
      ? buildExpectedMediaNames(item.program.programCode, scheduledDate)
      : [],
    [item.program?.programCode, scheduledDate],
  )
  const autoMediaCandidate = useMemo(() => {
    const candidates = new Set(expectedMediaNames.map(name => name.toLocaleLowerCase('fr-FR')))
    return [...programMedia].reverse().find(asset => candidates.has(asset.fileName.toLocaleLowerCase('fr-FR'))) ?? null
  }, [expectedMediaNames, programMedia])
  const currentMedia = programMedia.find(asset => asset.id === item.mediaAssetId) ?? item.mediaAsset ?? null
  const previousAirings = useMemo(() => {
    const currentStart = new Date(item.startTime).getTime()
    return (programDetail?.asRunHistory ?? []).filter(log => {
      const loggedStart = new Date(log.scheduledStart).getTime()
      return !Number.isFinite(currentStart) || !Number.isFinite(loggedStart) || Math.abs(loggedStart - currentStart) > 1000
    })
  }, [item.startTime, programDetail?.asRunHistory])
  const expectedFileName = expectedMediaNames[0] ?? null
  const mediaPresentation = getMediaPresentation(currentMedia?.status ?? null, Boolean(item.mediaAssetId))
  const adOrderDirty = useMemo(() => {
    if (adOrder.length !== savedAdOrderIds.length) return true
    return adOrder.some((p, idx) => p.id !== savedAdOrderIds[idx])
  }, [adOrder, savedAdOrderIds])

  const handleAutoAssociate = useCallback(async () => {
    if (!canManageMedia || !item.programId) return
    setMediaBusy('auto')
    setMediaError(null)
    try {
      const updated = autoMediaCandidate
        ? await itemsApi.update(item.id, { mediaAssetId: autoMediaCandidate.id })
        : await itemsApi.update(item.id, {})
      if (!updated.mediaAsset) {
        throw new Error(expectedFileName
          ? `Aucun fichier « ${expectedFileName} » n’a été trouvé. Choisissez-le dans la bibliothèque.`
          : 'Aucun fichier n’a été trouvé automatiquement. Ajoutez un code programme ou choisissez la bibliothèque.')
      }
      onItemUpdated(updated)
      await loadProgramDetail()
    } catch (err: any) {
      setMediaError(err?.message ?? 'Impossible d’associer le fichier automatiquement.')
    } finally {
      setMediaBusy(null)
    }
  }, [autoMediaCandidate, canManageMedia, expectedFileName, item.id, item.programId, loadProgramDetail, onItemUpdated])

  const handleCheckMedia = useCallback(async () => {
    if (!canManageMedia || !currentMedia) return
    setMediaBusy('check')
    setMediaError(null)
    setMediaCheck(null)
    try {
      setMediaCheck(await programsApi.checkMedia(currentMedia.id))
      const updated = await itemsApi.update(item.id, {})
      onItemUpdated(updated)
      await loadProgramDetail()
    } catch (err: any) {
      setMediaError(err?.message ?? 'La vérification du fichier a échoué.')
    } finally {
      setMediaBusy(null)
    }
  }, [canManageMedia, currentMedia, item.id, loadProgramDetail, onItemUpdated])

  // Le fichier choisi dans la bibliothèque attend la réponse de l'opérateur :
  // « Appliquer à toutes les diffusions à venir ? ». Oui : il devient le
  // fichier de la fiche, qui fait foi, et toutes les diffusions le suivent.
  // Non : il ne vaut que pour cette diffusion.
  const [pendingMedia, setPendingMedia] = useState<SelectedMedia | null>(null)

  const handleMediaSelected = useCallback((result: SelectedMedia) => {
    if (!item.programId || !canManageMedia) return
    setMediaBrowserOpen(false)
    setPendingMedia(result)
  }, [canManageMedia, item.programId])

  const applyPendingMedia = useCallback(async (toAllAirings: boolean) => {
    const result = pendingMedia
    if (!result || !item.programId || !canManageMedia) return
    setPendingMedia(null)
    setMediaBusy('browse')
    setMediaError(null)
    const fileData = {
      filePath: result.filePath,
      fileName: result.fileName,
      codec: result.codec ?? undefined,
      container: result.container ?? undefined,
      durationSec: result.durationSec ?? undefined,
      resolution: result.resolution ?? undefined,
      fileSizeBytes: result.fileSizeBytes ?? undefined,
    }
    try {
      if (toAllAirings) {
        const asset = await programsApi.addMedia(item.programId, { ...fileData, isProxy: false })
        const updated = await itemsApi.update(item.id, { mediaAssetId: asset.id })
        onItemUpdated(updated)
      } else {
        onItemUpdated(await itemsApi.assignMediaOnly(item.id, fileData))
      }
      await loadProgramDetail()
    } catch (err: any) {
      setMediaError(err?.message ?? 'Impossible d’associer ce fichier.')
    } finally {
      setMediaBusy(null)
    }
  }, [canManageMedia, item.id, item.programId, loadProgramDetail, onItemUpdated, pendingMedia])

  const moveAdPlacement = useCallback((activeId: string, overId: string) => {
    if (!canEditBlock) return
    setAdOrder(prev => {
      const oldIndex = prev.findIndex(p => p.id === activeId)
      const newIndex = prev.findIndex(p => p.id === overId)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [canEditBlock])

  const handleSaveAdOrder = useCallback(async () => {
    if (!canEditBlock || !adOrderDirty) return
    setSavingAdOrder(true)
    setAdOrderError(null)
    try {
      await adBlocksApi.updateItemSpots(item.id, adOrder.map(p => p.spot.id))
      pushToast('Ordre du bloc pub mis a jour', 'success', 4000)
      setSavedAdOrderIds(adOrder.map(p => p.id))
      incrementGridUpdateSeq()
    } catch (err: any) {
      setAdOrderError(err?.message ?? 'Impossible de sauvegarder l ordre du bloc')
    } finally {
      setSavingAdOrder(false)
    }
  }, [adOrder, adOrderDirty, canEditBlock, incrementGridUpdateSeq, item.id, pushToast])

  return (
    <>
      <DrawerShell
        className="tp-item-detail item-detail-drawer"
        headerMeta={(
          <div className={`item-detail__badges is-${item.status.toLowerCase().replace('_', '-')}`}>
            <span className="item-detail__badge item-detail__badge--type">{ITEM_TYPE_LABELS[item.itemType]}</span>
            <span className="item-detail__badge item-detail__badge--status">{ITEM_STATUS_LABELS[item.status]}</span>
          </div>
        )}
        onClose={handleClose}
        subtitle={(
          <div className="tp-item-detail__schedule">
            <span className="tp-item-detail__schedule-line">
              <CalendarDays size={13} aria-hidden="true" />
              <span className="item-detail__date">{scheduledDateLabel}</span>
            </span>
            <span className="tp-item-detail__schedule-line tp-item-detail__schedule-line--mono">
              <Clock3 size={13} aria-hidden="true" />
              <span>{formatTime(item.startTime)} → {formatTime(item.endTime)}</span>
              <span aria-hidden="true">·</span>
              <span>{formatDuration(item.durationSec)}</span>
            </span>
          </div>
        )}
        title={title}
        visible={visible}
      >
        <div className="item-detail__body">

        {/* ── Programme ────────────────────────────────────────────────── */}
        {item.itemType === 'LIVE' && item.programId && (
          <div className="item-detail__hint">
            Lié à une fiche programme : les modifications faites via « Modifier » sont propagées au programme de rediffusion.
          </div>
        )}

        {/* ── Déroulé estimé d'un bloc de direct ──────────────────────── */}
        {livePartTimeline && (
          <Section title={`Déroulé estimé (${livePartTimeline.segments.length} parties)`}>
            {livePartTimeline.segments.map(segment => (
              <Row
                key={segment.number}
                label={segment.title ?? `Partie ${segment.number}`}
                value={`${formatTime(new Date(segment.startMs).toISOString())} → ${formatTime(new Date(segment.endMs).toISOString())}`}
                mono
              />
            ))}
            <div className="item-detail__hint">
              Estimation : rien n’est posé dans le bloc. Elle sert de repère au chrono de régie
              et découpe la case publiée à l’EPG.
            </div>
          </Section>
        )}

        {item.program && (
          <Section title="Programme">
            <Row label="Titre"  value={item.program.title} />
            <Row label="Type"   value={item.program.type} />
            <Row label="Durée déclarée" value={formatDuration(item.program.durationSec)} />
          </Section>
        )}

        {/* ── Bloc pub : liste ordonnée des spots ─────────────────────── */}
        {isAdBlock && adOrder.length > 0 && (
          <Section title={adOrder.length > 1 ? `Spots du bloc (${adOrder.length})` : 'Spot pub'}>
            <div className="item-detail-ad-order">
              {adOrder.map((p, idx) => (
                <div
                  key={p.id}
                  draggable={canEditBlock}
                  onDragStart={e => {
                    setDraggedAdId(p.id)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragOver={e => {
                    if (!canEditBlock || !draggedAdId || draggedAdId === p.id) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                  }}
                  onDrop={e => {
                    e.preventDefault()
                    if (draggedAdId) moveAdPlacement(draggedAdId, p.id)
                    setDraggedAdId(null)
                  }}
                  onDragEnd={() => setDraggedAdId(null)}
                  className={`item-detail-ad-order__row${draggedAdId === p.id ? ' is-dragging' : ''}${canEditBlock ? ' is-editable' : ''}`}
                >
                  <span className="item-detail-ad-order__index">
                    {idx + 1}.
                  </span>
                  <div className="item-detail-ad-order__main">
                    <div className="item-detail-ad-order__title">
                      {formatAdSpotLabel(p.spot)}
                    </div>
                    {p.spot.campaign?.advertiser?.name && (
                      <div className="item-detail-ad-order__meta">
                        {p.spot.campaign.advertiser.name}
                      </div>
                    )}
                  </div>
                  <span className="item-detail-ad-order__duration">
                    {p.spot.durationSec}s
                  </span>
                </div>
              ))}
            </div>
            {adOrderError && (
              <div className="tp-item-detail__error" role="alert">
                {adOrderError}
              </div>
            )}
            {canEditBlock && adOrderDirty && (
              <button
                type="button"
                className="tp-button tp-button--success-outline item-detail__full-button"
                onClick={handleSaveAdOrder}
                disabled={savingAdOrder}
                data-state={savingAdOrder ? 'loading' : undefined}
              >
                {savingAdOrder ? 'Enregistrement...' : 'Enregistrer cet ordre'}
              </button>
            )}
            {canEditBlock && (
              <button
                type="button"
                className="tp-button tp-button--info item-detail__full-button"
                onClick={() => setShowBlockEditor(true)}
              >
                Modifier le bloc pub
              </button>
            )}
            {!canEditBlock && (
              <div className="item-detail__hint item-detail__hint--spaced">
                {item.status === 'ON_AIR'  ? 'Bloc en cours de diffusion — non modifiable.'
                 : item.status === 'DONE'   ? 'Bloc déjà diffusé — non modifiable.'
                 : item.status === 'LOADING' ? 'Bloc en chargement — non modifiable.'
                 : 'Bloc non modifiable.'}
              </div>
            )}
          </Section>
        )}

        {/* ── Média ────────────────────────────────────────────────────── */}
        {item.programId && item.itemType !== 'LIVE' && (
          <Section title="Fichier média">
            <div className={`tp-media-readiness is-${mediaPresentation.tone}`} aria-live="polite">
              <div className="tp-media-readiness__icon" aria-hidden="true">
                {mediaPresentation.tone === 'success'
                  ? <CheckCircle2 size={18} />
                  : mediaPresentation.tone === 'warning'
                    ? <AlertTriangle size={18} />
                    : currentMedia
                      ? <FileQuestion size={18} />
                      : <FileVideo2 size={18} />}
              </div>
              <div className="tp-media-readiness__body">
                <div className="tp-media-readiness__status">{mediaPresentation.label}</div>
                <div className="tp-media-readiness__help">{mediaPresentation.help}</div>
              </div>
            </div>

            <div className="tp-media-file">
              <div className="tp-media-file__name">
                {currentMedia?.fileName ?? 'Aucun fichier associé à cette diffusion'}
              </div>
              {currentMedia ? (
                <div className="tp-media-file__meta">
                  <span>{currentMedia.codec ?? 'Codec non renseigné'}</span>
                  {currentMedia.durationSec ? <span>{formatDuration(currentMedia.durationSec)}</span> : null}
                </div>
              ) : autoMediaCandidate ? (
                <div className="tp-media-file__candidate">
                  Trouvé dans le catalogue : <span>{autoMediaCandidate.fileName}</span>
                </div>
              ) : expectedFileName ? (
                <div className="tp-media-file__candidate">
                  Nom attendu : <span>{expectedFileName}</span>
                </div>
              ) : null}
            </div>

            {mediaError && (
              <div className="tp-item-detail__error" role="alert">{mediaError}</div>
            )}

            {mediaCheck && (
              <div
                className={`tp-item-detail__error${mediaCheckTone(mediaCheck)}`}
                role="status"
              >
                {mediaCheck.issues.length === 0
                  ? (mediaCheck.audio
                      ? 'Fichier compatible CasparCG — audio décodé sans erreur'
                      : 'Fichier compatible CasparCG')
                  : mediaCheck.issues.map((issue, index) => <div key={index}>{issue}</div>)}
              </div>
            )}

            {canManageMedia && (
              <div className="tp-media-actions">
                {!currentMedia && (
                  <button
                    className="tp-button tp-button--info"
                    onClick={handleAutoAssociate}
                    disabled={mediaBusy !== null}
                    data-state={mediaBusy === 'auto' ? 'loading' : undefined}
                  >
                    {mediaBusy === 'auto' ? <RefreshCw size={13} aria-hidden="true" /> : <Search size={13} aria-hidden="true" />}
                    {mediaBusy === 'auto' ? 'Recherche…' : autoMediaCandidate ? 'Associer' : 'Rechercher'}
                  </button>
                )}
                <button
                  className="tp-button"
                  onClick={() => setMediaBrowserOpen(true)}
                  disabled={mediaBusy !== null}
                >
                  <FolderOpen size={13} aria-hidden="true" />
                  Bibliothèque
                </button>
                {currentMedia && (
                  <button
                    className="tp-button"
                    onClick={handleCheckMedia}
                    disabled={mediaBusy !== null}
                    data-state={mediaBusy === 'check' ? 'loading' : undefined}
                  >
                    <RefreshCw size={13} aria-hidden="true" />
                    {mediaBusy === 'check' ? 'Vérification…' : 'Vérifier'}
                  </button>
                )}
              </div>
            )}
          </Section>
        )}

        {/* ── Source live ─────────────────────────────────────────────── */}
        {item.liveSource && (
          <Section title="Source live">
            <Row label="Nom"    value={item.liveSource.name} />
            <Row label="Type"   value={item.liveSource.sourceType === 'DECKLINK' ? 'Decklink' : 'Internet'} />
            <Row label="Statut" value={
              item.liveSource.status === 'ACTIVE'      ? 'Actif' :
              item.liveSource.status === 'SIGNAL_LOST' ? 'Signal perdu' :
              item.liveSource.status === 'UNSTABLE'    ? 'Instable' : 'Inactif'
            } />
          </Section>
        )}

        {/* ── AsRun (si diffusé) ───────────────────────────────────────── */}
        {item.programId && (
          <Section title={previousAirings.length > 0 ? `Diffusions précédentes (${previousAirings.length})` : 'Diffusions précédentes'}>
            {programLoading && (
              <div className="tp-item-detail__muted"><Spinner /> Chargement de l’historique…</div>
            )}
            {!programLoading && programError && (
              <div className="tp-item-detail__error" role="alert">
                <span>{programError}</span>
                <button className="tp-button" onClick={() => { void loadProgramDetail() }}>Réessayer</button>
              </div>
            )}
            {!programLoading && !programError && previousAirings.length === 0 && (
              <div className="tp-airing-history__empty">
                <History size={16} aria-hidden="true" />
                <span>Aucune diffusion enregistrée pour ce programme.</span>
              </div>
            )}
            {!programLoading && !programError && previousAirings.length > 0 && (
              <div className="tp-airing-history">
                {previousAirings.slice(0, 5).map(log => (
                  <div className="tp-airing-history__row" key={log.id}>
                    <div className="tp-airing-history__main">
                      <span className="tp-airing-history__date">{formatAiringDate(log.actualStart ?? log.scheduledStart)}</span>
                      <span className="tp-airing-history__time">
                        {formatTime(log.actualStart ?? log.scheduledStart)}
                        {log.actualEnd ? ` → ${formatTime(log.actualEnd)}` : ''}
                      </span>
                    </div>
                    <div className="tp-airing-history__result">
                      <span className={`tp-airing-history__status is-${airingStatusTone(log.status)}`}>
                        {formatAiringStatus(log.status)}
                      </span>
                      {log.durationDiffSec != null && (
                        <span className="tp-airing-history__drift">
                          {log.durationDiffSec >= 0 ? '+' : ''}{log.durationDiffSec}s
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {previousAirings.length > 5 && (
                  <div className="tp-airing-history__more">
                    5 dernières affichées sur {previousAirings.length}
                  </div>
                )}
              </div>
            )}
          </Section>
        )}

        {item.asRunLog && (
          <Section title="Diffusion réelle">
            {item.asRunLog.actualStart && (
              <Row label="Début réel" value={formatTime(item.asRunLog.actualStart)} />
            )}
            {item.asRunLog.actualEnd && (
              <Row label="Fin réelle" value={formatTime(item.asRunLog.actualEnd)} />
            )}
            {item.asRunLog.durationDiffSec != null && (
              <Row
                label="Écart"
                value={`${item.asRunLog.durationDiffSec >= 0 ? '+' : ''}${item.asRunLog.durationDiffSec}s`}
                highlight={Math.abs(item.asRunLog.durationDiffSec) > 30}
              />
            )}
          </Section>
        )}

        {/* ── Récurrence ──────────────────────────────────────────────── */}
        {item.recurrenceId && (
          <Section title="Récurrence">
            <div className="item-detail__recurrence">
              <span className="item-detail__recurrence-icon" aria-hidden="true">↻</span>
              <span>
                Événement récurrent
                {item.recurrenceIndex !== null && ` · occurrence #${(item.recurrenceIndex ?? 0) + 1}`}
              </span>
            </div>
          </Section>
        )}

        {/* ── Notes ───────────────────────────────────────────────────── */}
        {item.notes && (
          <Section title="Notes">
            <div className="item-detail__notes">{item.notes}</div>
          </Section>
        )}

        {/* Espace flexible */}
        <div className="item-detail__spacer" />

        {/* ── Actions ──────────────────────────────────────────────────── */}
        {!isLocked && (
          <div className="tp-item-detail__actions">
            {canUseScheduleEdit && (
              <button
                className="tp-button tp-button--primary"
                onClick={() => onEdit(item)}
                disabled={!canEditPermission}
                title={!canEditPermission ? 'Permission GRID_ITEM_EDIT requise' : undefined}
              >
                <Pencil size={13} aria-hidden="true" />
                Modifier
              </button>
            )}
            <button
              className="tp-button tp-button--danger"
              onClick={handleDeleteClick}
              disabled={deleting || !canDelete}
              title={!canDelete ? 'Permission GRID_ITEM_DELETE requise' : undefined}
              data-state={deleting ? 'loading' : undefined}
            >
              <Trash2 size={13} aria-hidden="true" />
              {deleting ? 'Suppression…' : 'Retirer'}
            </button>
          </div>
        )}

        {/* ── Dialog portée du fichier choisi ─────────────────────────── */}
        {pendingMedia && (
          <ModalShell
            className="agenda-editor item-recurrence-dialog"
            dismissOnOverlay={false}
            onClose={() => setPendingMedia(null)}
            showCloseButton={false}
            size="narrow"
            title="Appliquer à toutes les diffusions à venir ?"
          >
            <div className="item-recurrence-dialog__body">
              <div className="item-recurrence-dialog__copy">
                « {pendingMedia.fileName} » remplace le fichier de cette diffusion.
                Oui : il devient le fichier de la fiche « {item.program?.title ?? title} », et toutes ses
                diffusions à venir le prennent. Non : seule cette diffusion change.
              </div>
              <div className="item-recurrence-dialog__actions">
                <button
                  type="button"
                  className="tp-button tp-button--primary item-recurrence-dialog__choice"
                  onClick={() => void applyPendingMedia(true)}
                >
                  Oui, toutes les diffusions et la fiche
                </button>
                <button
                  type="button"
                  className="tp-button item-recurrence-dialog__choice"
                  onClick={() => void applyPendingMedia(false)}
                >
                  Non, cette diffusion uniquement
                </button>
                <button
                  type="button"
                  className="tp-button item-recurrence-dialog__cancel"
                  onClick={() => setPendingMedia(null)}
                >
                  Annuler
                </button>
              </div>
            </div>
          </ModalShell>
        )}

        {/* ── Dialog récurrence ────────────────────────────────────────── */}
        {showRecDialog && (
          <ModalShell
            className="agenda-editor item-recurrence-dialog"
            dismissOnOverlay={false}
            onClose={() => setShowRecDialog(false)}
            showCloseButton={false}
            size="narrow"
            title="Supprimer un événement récurrent"
          >
              <div className="item-recurrence-dialog__body">
              <div className="item-recurrence-dialog__copy">
                Cet événement fait partie d'une série. Que souhaitez-vous supprimer ?
              </div>
              <div className="item-recurrence-dialog__actions">
                <button
                  type="button"
                  className="tp-button item-recurrence-dialog__choice"
                  onClick={handleDeleteSingle}
                >
                  Cet événement uniquement
                </button>
                <button
                  type="button"
                  className="tp-button item-recurrence-dialog__choice"
                  onClick={handleDeleteFromHere}
                >
                  Cet événement et les suivants
                </button>
                <button
                  type="button"
                  className="tp-button tp-button--danger-outline item-recurrence-dialog__choice"
                  onClick={handleDeleteAll}
                >
                  Toute la série
                </button>
                <button
                  type="button"
                  className="tp-button item-recurrence-dialog__cancel"
                  onClick={() => setShowRecDialog(false)}
                >
                  Annuler
                </button>
              </div>
              </div>
          </ModalShell>
        )}

        {isLocked && (
          <div className="item-detail__locked-note">
            Cet item {item.status === 'ON_AIR'
              ? 'est en cours de diffusion'
              : item.status === 'LOADING'
                ? 'est en cours de chargement'
                : 'a déjà été diffusé'} — modification impossible
          </div>
        )}
        </div>
      </DrawerShell>

      {mediaBrowserOpen && canManageMedia && (
        <MediaBrowserModal
          onSelect={handleMediaSelected}
          onClose={() => setMediaBrowserOpen(false)}
        />
      )}

      {/* ── Modal édition bloc pub ─────────────────────────────────────── */}
      {showBlockEditor && channelId && (
        <AdBlockEditModal
          itemId={item.id}
          channelId={channelId}
          onClose={() => setShowBlockEditor(false)}
          onSaved={() => { setShowBlockEditor(false); onClose() }}
        />
      )}
    </>
  )
})

// ─── Sous-composants ──────────────────────────────────────────────────────────

function buildExpectedMediaNames(programCode: string, date: Date): string[] {
  const yyyy = String(date.getFullYear()).padStart(4, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const code = stripProgramCodePrefix(programCode.toUpperCase())
  return [`${yyyy}${mm}${dd}_${code}.mp4`]
}

function getMediaPresentation(status: string | null, associated: boolean): {
  tone: 'success' | 'warning' | 'danger'
  label: string
  help: string
} {
  if (!associated || !status) {
    return {
      tone: 'danger',
      label: 'Fichier absent',
      help: 'Cette diffusion ne pourra pas être chargée tant qu’un fichier n’est pas associé.',
    }
  }
  if (status === 'OK') {
    return {
      tone: 'success',
      label: 'Prêt à diffuser',
      help: 'Le fichier est associé et sa conformité a été validée.',
    }
  }
  if (status === 'UNCHECKED') {
    return {
      tone: 'warning',
      label: 'Fichier à vérifier',
      help: 'Le fichier est associé, mais sa conformité n’a pas encore été validée.',
    }
  }
  if (status === 'MISSING') {
    return {
      tone: 'danger',
      label: 'Fichier introuvable',
      help: 'Le chemin enregistré ne pointe plus vers un fichier accessible.',
    }
  }
  if (status === 'INVALID_CODEC') {
    return {
      tone: 'danger',
      label: 'Codec incompatible',
      help: 'Le fichier est associé, mais il doit être converti avant diffusion.',
    }
  }
  return {
    tone: 'warning',
    label: 'Durée à contrôler',
    help: 'La durée du fichier diffère de la durée déclarée dans la grille.',
  }
}

function formatAiringDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  })
}

function formatAiringStatus(status: string): string {
  if (status === 'OK') return 'Diffusé'
  if (status === 'GAP_FILLED') return 'Gap comblé'
  if (status === 'OVERRUN') return 'Dépassement'
  if (status === 'MANUAL_OVERRIDE') return 'Manuel'
  return 'Erreur'
}

/**
 * Modificateur de ton du bandeau de vérification.
 *
 * Une piste audio corrompue ou muette est aussi bloquante qu'un codec refusé,
 * alors même qu'elle ne dégrade pas le statut diffusable du média : sans ce
 * traitement, le défaut s'afficherait en vert.
 */
function mediaCheckTone(result: MediaCheckResult): string {
  if (result.issues.length === 0) return ' tp-item-detail__error--success'
  const blocking = result.status !== 'OK' || result.audio?.severity === 'ERROR'
  return blocking ? '' : ' tp-item-detail__error--warning'
}

function airingStatusTone(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'OK') return 'success'
  if (['GAP_FILLED', 'OVERRUN', 'MANUAL_OVERRIDE'].includes(status)) return 'warning'
  return 'danger'
}

const Section = memo(function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="item-detail-section">
      <h3 className="agenda-editor__eyebrow item-detail-section__title">
        {title}
      </h3>
      {children}
    </section>
  )
})

const Row = memo(function Row({
  label, value, mono, highlight,
}: { label: string; value: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div className="item-detail-row">
      <span className="item-detail-row__label">{label}</span>
      <span className={`item-detail-row__value${mono ? ' is-mono' : ''}${highlight ? ' is-highlighted' : ''}`}>
        {value}
      </span>
    </div>
  )
})
